/**
 * TodayBadge — Widget de cabecera "Hoy"
 *
 * Badge rojo pulsante con contador de eventos de hoy.
 *
 * Comportamiento por rol:
 *   - Admin: usa el contexto global `playlists` (ya cargado) y muestra TODOS
 *     los eventos de hoy para supervisión del equipo. Tap con count===1 →
 *     abre el detalle del evento (sin activar Modo En Vivo). count>=2 →
 *     navega al dashboard y hace scroll a la sección "Hoy".
 *   - DJ/Asistente: fetchea `/api/users/my-assignments` (solo los suyos).
 *     Tap con count===1 → abre + activa Modo En Vivo en 1 tap (flujo original).
 *
 * Si no hay eventos hoy → renderiza null (cero ruido visual).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config';
import { useAuth } from '../AuthContext';
import { usePlaylistsContext } from '../contexts/PlaylistsContext';
import { CalendarCheck } from 'lucide-react';

const API = API_URL;
const REFRESH_MS = 5 * 60 * 1000;

const todayIsoLocal = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const onlyDate = (v) => (v ? String(v).split('T')[0] : '');

const sortByStartTime = (a, b) => {
  const ta = a.horario_inicio || '99:99';
  const tb = b.horario_inicio || '99:99';
  return ta.localeCompare(tb);
};

export const TodayBadge = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { playlists } = usePlaylistsContext();
  const [djEvents, setDjEvents] = useState([]);

  const isAdmin = user?.role === 'admin';
  const isLogged = !!user;

  // ADMIN: derivar eventos de hoy desde el contexto global (ya cargado).
  const adminTodayEvents = useMemo(() => {
    if (!isAdmin) return [];
    const today = todayIsoLocal();
    return (playlists || [])
      .filter((p) => onlyDate(p.fecha_evento) === today)
      .sort(sortByStartTime);
  }, [isAdmin, playlists]);

  // DJ: fetch propio de sus asignaciones.
  const load = useCallback(async () => {
    if (!isLogged || isAdmin) return;
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await axios.get(`${API}/users/my-assignments`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const assignments = res.data?.assignments || [];
      const today = todayIsoLocal();
      const filtered = assignments
        .filter((a) => onlyDate(a.fecha_evento) === today)
        .sort(sortByStartTime);
      setDjEvents(filtered);
    } catch (err) {
      console.warn('[TodayBadge] Error cargando asignaciones:', err?.message);
    }
  }, [isLogged, isAdmin]);

  useEffect(() => {
    if (!isLogged || isAdmin) return;
    load();
    const interval = setInterval(load, REFRESH_MS);
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [isLogged, isAdmin, load]);

  const events = isAdmin ? adminTodayEvents : djEvents;
  const count = events.length;

  if (!isLogged || count === 0) return null;

  const handleClick = () => {
    if (count === 1) {
      const ev = events[0];
      if (isAdmin) {
        // Admin: abre el detalle para supervisar (sin forzar Modo En Vivo).
        navigate(`/?openEvent=${encodeURIComponent(ev.id)}`);
      } else {
        // DJ: abre + activa Modo En Vivo en 1 tap.
        navigate(`/?openEvent=${encodeURIComponent(ev.id)}&liveMode=1`);
      }
    } else {
      // count >= 2 → al dashboard correspondiente con scroll a Vista Hoy.
      const anchor = isAdmin ? '/dashboard#today-dj-view' : '/#today-dj-view';
      navigate(anchor);
      setTimeout(() => {
        const el = document.querySelector('[data-testid="today-dj-view"]');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 200);
    }
  };

  const label = count === 1
    ? `Hoy · ${events[0].horario_inicio || 'Evento'}`
    : `Hoy · ${count}`;

  const title = count === 1
    ? (isAdmin
        ? `Ver evento de hoy (${events[0].nombre_novios || 'sin nombre'})`
        : `Abrir tu evento de hoy (${events[0].nombre_novios || 'sin nombre'}) en Modo En Vivo`)
    : (isAdmin
        ? `${count} eventos hoy en el equipo — ver detalle`
        : `Tienes ${count} eventos hoy — ir a Vista Hoy DJ`);

  return (
    <button
      type="button"
      onClick={handleClick}
      className="relative inline-flex items-center gap-1.5 rounded-full bg-red-600 hover:bg-red-500 text-white text-xs sm:text-sm font-semibold px-2.5 sm:px-3 py-1 sm:py-1.5 shadow-sm shadow-red-900/40 transition-colors focus:outline-none focus:ring-2 focus:ring-red-400/60"
      title={title}
      data-testid="today-header-badge"
      data-count={count}
      data-role={isAdmin ? 'admin' : 'dj'}
    >
      <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-red-300 animate-ping" aria-hidden />
      <CalendarCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
      <span className="whitespace-nowrap">{label}</span>
    </button>
  );
};

export default TodayBadge;
